import './style.css';
import { runBoot } from './fx/boot';
import { initChrome } from './fx/chrome';
import { initDust } from './fx/dust';
import { initReveal } from './fx/reveal';
import { scrambleAll } from './scramble';

type Dossier = {
  eyebrow: string;
  title: string;
  meta: string;
  body: string;
};

const dossiers: Record<string, Dossier> = {
  contra: {
    eyebrow: 'Project / R&D',
    title: 'Contra',
    meta: 'networking / distributed infrastructure / prototype',
    body: `
      <p>P2P UDP traversal and blockchain STUN rendezvous research. A prototype for connection paths that need to remain reliable across challenging network conditions.</p>
      <dl class="spec-list">
        <div><dt>Status</dt><dd>Prototype</dd></div>
        <div><dt>Surface</dt><dd>Networking, rendezvous, low-level transport</dd></div>
        <div><dt>Disclosure</dt><dd>Limited public summary</dd></div>
      </dl>
    `
  },
  'undisclosed-logistics': {
    eyebrow: 'Project / Confidential',
    title: 'Undisclosed',
    meta: 'internal tooling / operations software / client confidential',
    body: `
      <p>Logistics routing and financial operations hub for food supply lines. Designed around real operational constraints, permissions, and edge cases.</p>
      <dl class="spec-list">
        <div><dt>Status</dt><dd>Client system</dd></div>
        <div><dt>Surface</dt><dd>Routing, finance workflows, operational visibility</dd></div>
        <div><dt>Disclosure</dt><dd>Limited by client agreement</dd></div>
      </dl>
    `
  },
  nokk: {
    eyebrow: 'Project / Client',
    title: 'Nokk',
    meta: 'data enrichment / outreach tooling / mobile workflow',
    body: `
      <p>B2SMB enrichment and outreach tooling shaped around mobile work patterns, list quality, and team follow-through.</p>
      <dl class="spec-list">
        <div><dt>Status</dt><dd>Client build</dd></div>
        <div><dt>Surface</dt><dd>Enrichment, outreach, mobile workflow</dd></div>
        <div><dt>Disclosure</dt><dd>Selectively shared</dd></div>
      </dl>
    `
  },
  'crypto-citizen': {
    eyebrow: 'Project / Prototype',
    title: 'Crypto Citizen',
    meta: 'game systems / economy design / prototype',
    body: `
      <p>2D pixel-art building game with economy systems backed by real cryptocurrency. A prototype where game loop, ownership, and resource flow meet.</p>
      <dl class="spec-list">
        <div><dt>Status</dt><dd>Prototype</dd></div>
        <div><dt>Surface</dt><dd>Game systems, economy, wallet-aware mechanics</dd></div>
        <div><dt>Disclosure</dt><dd>In progress</dd></div>
      </dl>
    `
  },
  'undisclosed-education': {
    eyebrow: 'Project / Confidential',
    title: 'Undisclosed',
    meta: 'education tooling / simulation / public-sector research',
    body: `
      <p>Cross-domain civil-engineering education system for infrastructure training and simulation. Public details remain limited.</p>
      <dl class="spec-list">
        <div><dt>Status</dt><dd>Client confidential</dd></div>
        <div><dt>Surface</dt><dd>Simulation, education tooling, research support</dd></div>
        <div><dt>Disclosure</dt><dd>Limited by client agreement</dd></div>
      </dl>
    `
  },
  sprint: {
    eyebrow: 'Method',
    title: 'Sprint Process',
    meta: 'short build cycles / practical systems / clean handoff',
    body: `
      <p>Fayestar sprints are focused build cycles for turning complex operational needs into working internal systems.</p>
      <ol class="protocol-list">
        <li><strong>Identify</strong><span>Clarify the workflow, goals, constraints, and opportunities for a useful first version.</span></li>
        <li><strong>Map</strong><span>Document the users, data, tools, permissions, automations, edge cases, and key requirements. Define the right initial scope.</span></li>
        <li><strong>Build</strong><span>Create the first working version: internal tools, dashboards, automations, scripts, integrations, databases, or custom interfaces.</span></li>
        <li><strong>Validate</strong><span>Review the system against real data and day-to-day usage. Refine the details that matter most.</span></li>
        <li><strong>Stabilize</strong><span>Add reliability: permissions, error states, logging, cleanup, documentation, performance, and deployment polish.</span></li>
        <li><strong>Deliver</strong><span>Provide the working system, source access, notes, and a practical next-step plan. Some engagements conclude here; others continue into broader infrastructure.</span></li>
      </ol>
    `
  },
  apply: {
    eyebrow: 'Project inquiry',
    title: 'Work with Fayestar',
    meta: 'private clientele / selective availability / direct reply',
    body: `
      <p>Send a brief overview of your goals, scope, timeline, and any relevant confidentiality requirements. We review each inquiry carefully and follow up when there is a strong fit.</p>
      <form class="intake-form" data-intake-form novalidate>
        <label>
          <span>Email</span>
          <input type="email" name="signal" autocomplete="email" placeholder="you@domain.com" required />
        </label>
        <label>
          <span>Subject</span>
          <input type="text" name="subject" autocomplete="off" maxlength="200" placeholder="Internal tool / automation / prototype" required />
        </label>
        <label>
          <span>Project overview</span>
          <textarea name="body" rows="6" maxlength="5000" placeholder="Briefly describe the system, prototype, automation, or workflow you are considering." required></textarea>
        </label>
        <input class="hp" type="text" name="hp" tabindex="-1" autocomplete="off" aria-hidden="true" />
        <p class="form-error" data-form-error hidden></p>
        <button class="btn btn--primary" type="submit"><span>Send inquiry</span></button>
      </form>
    `
  }
};

const root = document.getElementById('dossier');
const titleEl = root?.querySelector<HTMLElement>('[data-dossier-title]');
const eyebrowEl = root?.querySelector<HTMLElement>('[data-dossier-eyebrow]');
const metaEl = root?.querySelector<HTMLElement>('[data-dossier-meta]');
const bodyEl = root?.querySelector<HTMLElement>('[data-dossier-body]');
const closeButtons = root?.querySelectorAll<HTMLElement>('[data-close-dossier]') ?? [];
let lastTrigger: HTMLElement | null = null;

document.querySelectorAll<HTMLElement>('[data-dossier]').forEach((trigger) => {
  trigger.addEventListener('click', () => {
    const id = trigger.dataset.dossier;
    if (!id) return;
    openDossier(id, trigger);
  });
});

closeButtons.forEach((button) => {
  button.addEventListener('click', closeDossier);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && root?.dataset.open === 'true') {
    closeDossier();
  }
});

function openDossier(id: string, trigger: HTMLElement) {
  const dossier = dossiers[id];
  if (!root || !titleEl || !eyebrowEl || !metaEl || !bodyEl || !dossier) return;

  lastTrigger = trigger;
  eyebrowEl.textContent = dossier.eyebrow;
  titleEl.textContent = dossier.title;
  metaEl.textContent = dossier.meta;
  bodyEl.innerHTML = dossier.body;
  root.dataset.open = 'true';
  root.setAttribute('aria-hidden', 'false');
  document.body.classList.add('dossier-open');
  wireIntakeForm(bodyEl);
  root.querySelector<HTMLButtonElement>('.dossier__close')?.focus();
}

function closeDossier() {
  if (!root) return;
  root.dataset.open = 'false';
  root.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('dossier-open');
  lastTrigger?.focus();
}

function wireIntakeForm(scope: HTMLElement) {
  const form = scope.querySelector<HTMLFormElement>('[data-intake-form]');
  if (!form) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const signal = String(data.get('signal') ?? '').trim();
    const subject = String(data.get('subject') ?? '').trim();
    const body = String(data.get('body') ?? '').trim();
    const hp = String(data.get('hp') ?? '').trim();
    const errorEl = form.querySelector<HTMLElement>('[data-form-error]');
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    const label = submit?.querySelector('span');

    const showError = (message: string) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    };

    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    if (!signal || !subject || !body) {
      showError('Email, subject, and project overview are required.');
      return;
    }
    if (!/^.+@.+\..+$/.test(signal)) {
      showError('Email must be a valid return address.');
      return;
    }

    if (submit) submit.disabled = true;
    if (label) label.textContent = 'Sending...';

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signal, subject, body, hp })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      form.outerHTML = `
        <div class="sent-state">
          <strong>Inquiry received.</strong>
          <p>We will reply to the email provided.</p>
          <dl class="spec-list">
            <div><dt>Email</dt><dd>${escapeHtml(signal)}</dd></div>
            <div><dt>Subject</dt><dd>${escapeHtml(subject)}</dd></div>
            <div><dt>Status</dt><dd>Received</dd></div>
          </dl>
        </div>
      `;
    } catch (error) {
      console.error('[fayestar] intake failed:', error);
      showError('Message could not be sent. Please try again or use another contact method.');
      if (submit) submit.disabled = false;
      if (label) label.textContent = 'Retry message';
    }
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return char;
    }
  });
}

/* ── boot + entrance choreography ─────────────────────────────── */
async function start(): Promise<void> {
  const body = document.body;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  body.classList.add('is-booting');
  initChrome();
  initReveal();
  initDust(document.querySelector<HTMLCanvasElement>('[data-dust]'));

  await runBoot();

  body.classList.remove('is-booting');
  body.classList.add('is-ready');

  if (reduce) return;
  window.setTimeout(() => {
    void scrambleAll(document.querySelectorAll<HTMLElement>('[data-scramble]'), 0, 30);
  }, 180);
  window.setTimeout(() => {
    const late = document.querySelectorAll<HTMLElement>('[data-scramble-late]');
    late.forEach((el) => {
      el.dataset.scramble = el.dataset.scrambleLate;
    });
    void scrambleAll(late, 90, 24);
  }, 1250);
}

void start();
